import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { AlertCardComponent } from '@shared/components/alert-card/alert-card.component';
import { LoadingBoxComponent } from '@shared/components/loading-box/loading-box.component';
import { AbntPipe } from '@shared/pipes/abnt.pipe';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { ProcessoVendaService } from '@shared/services/apis/vendas/processo-venda.service';
import { ExcluirEstoqueVendaRequest } from '@shared/services/requests/vendas/excluir-estoque-venda.request';
import { EstoqueVendaMovimentacaoStruct } from '@shared/services/structs/vendas/estoque-venda-movimentacao.struct';

export interface PvExcluirEstoqueDialogData {
    idVenda: number;
    /** "Pedido nº 123" — o rótulo do cabeçalho. */
    rotuloDocumento: string;
}

/**
 * Excluir estoque do pedido (T11): mostra as movimentações que o pedido lançou no Produto, que voltam aos locais de
 * origem. Tudo ou nada: se algum saldo ficar negativo, o Produto recusa e nada é excluído. Fecha com `true` quando excluiu.
 */
@Component({
    selector: 'app-pv-excluir-estoque-dialog',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        MatDialogModule,
        MatButtonModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatProgressSpinnerModule,
        AlertCardComponent,
        LoadingBoxComponent,
        AbntPipe,
    ],
    templateUrl: './pv-excluir-estoque-dialog.component.html',
    styleUrls: ['./pv-excluir-estoque-dialog.component.css'],
})
export class PvExcluirEstoqueDialogComponent implements OnInit {
    public isLoading = true;
    public isExcluindo = false;
    public movimentacoes: EstoqueVendaMovimentacaoStruct[] = [];
    public justificativa = '';

    constructor(
        public dialogRef: MatDialogRef<PvExcluirEstoqueDialogComponent, boolean>,
        @Inject(MAT_DIALOG_DATA) public data: PvExcluirEstoqueDialogData,
        private processoVendaService: ProcessoVendaService,
        private alertService: AlertService,
    ) {}

    /** Sem o que o Produto tem do pedido não há o que mostrar: avisa por toast e fecha. */
    public ngOnInit(): void {
        this.processoVendaService.getEstoque(this.data.idVenda).subscribe({
            next: (res) => {
                this.isLoading = false;

                if (res.isError) {
                    this.alertService.show('Aviso', res.errorDescription || 'Não foi possível consultar o estoque do pedido.',
                        AlertType.warning);
                    this.dialogRef.close(false);
                    return;
                }

                // O card estava desatualizado (outro usuário excluiu): fecha com true para quem abriu recarregar.
                if (!res.isEstoqueLancado) {
                    this.alertService.show('Aviso', 'O estoque deste pedido não está lançado.', AlertType.warning);
                    this.dialogRef.close(true);
                    return;
                }

                this.movimentacoes = res.movimentacoes ?? [];
            },
            error: () => {
                this.isLoading = false;
                this.alertService.show('Aviso',
                    'Não foi possível consultar o estoque do pedido agora. Tente novamente em alguns instantes.', AlertType.warning);
                this.dialogRef.close(false);
            },
        });
    }

    public confirmar(): void {
        if (this.isLoading || this.isExcluindo) return;

        this.isExcluindo = true;
        const request = new ExcluirEstoqueVendaRequest();
        request.justificativa = this.justificativa.trim() || null;

        this.processoVendaService.excluirEstoque(this.data.idVenda, request).subscribe({
            next: (res) => {
                this.isExcluindo = false;

                if (res.isError) {
                    this.alertService.show('Aviso', res.errorDescription || 'Não foi possível excluir o estoque do pedido.',
                        AlertType.warning);
                    return;
                }

                if (res.avisoDescricao) {
                    this.alertService.show('Aviso', res.avisoDescricao, AlertType.warning);
                } else {
                    this.alertService.show('Estoque excluído', 'O saldo voltou aos locais de origem.', AlertType.success);
                }
                this.dialogRef.close(true);
            },
            error: () => {
                this.isExcluindo = false;
                this.alertService.show('Aviso',
                    'Não foi possível excluir o estoque agora. Tente novamente em alguns instantes.', AlertType.warning);
            },
        });
    }

    public fechar(): void {
        this.dialogRef.close(false);
    }
}
